import React, { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { debounce } from 'lodash';
import { Helmet } from 'react-helmet-async';
import FaqSection from '../../components/Faq/Faq';
import TopBusesRoutes from '../../components/TopBusesRoutes/TopBusesRoutes';
import Footer from '../../components/Footer/Footer';
// import CompactList from '../../components/CompactList/CompactList';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import PageHeader from '../../components/PageHeader/PageHeader';
import StarRatingBox from '../../components/StarRatingBox/StarRatingBox';
import ExtraDescription from '../../components/ExtraDescription/ExtraDescription';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import BusModifySearch from '../../components/BusModifySearch/BusModifySearch';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
import { RouteComponentProps } from 'react-router-dom';
import { CityTemplateData } from './CityDataTypeChecker';
import { EnvironmentVariables } from '../../types';
import { getAppEnv } from '../../../config/env';
import AppContextProvider from '../../AppContext';
import { trackCityTemplateEvent } from '../../utils/omnitureTracking';
import AdsComponent from '../../components/AdsComponent';
import './CityTemplate.css';
import OfferDropdown from '../../components/OfferDropdown/OfferDropdown';
import CompactListV1 from '../../components/CompactList/CompactListV1';
import SchemaInformation, {
  SchemaInformationTypes
} from '../../components/SchemaInformation/SchemaInformation';
import { yieldToMainThread } from '../../utils/scheduler';

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;

type PathParamsType = {
  cityCode: string;
};
type CityTemplateProp = RouteComponentProps<PathParamsType> & {
  data: CityTemplateData;
};
type MousePosType = {
  x: number;
  y: number;
};

const CityTemplate = ({ data }: CityTemplateProp) => {
  const {
    name,
    templateData,
    topOperatorsList,
    topRoutesToCity,
    topRoutesFromCity,
    otherRoutesFromCity,
    otherRoutesToCity,
    cityBoardingPointsList,
    cityDropPointsList,
    description,
    offers,
    code,
    nearbyCitiesSource,
    seoOfferData,
    schemaInfo
  } = data;

  const [collapsableFlag, setcollapsableFlag] = useState(false);
  const [fromCity, setFromCity] = useState('');
  const [toCity, setToCity] = useState(name && code ? name : '');
  const [fromCode, setFromCode] = useState('');
  const [toCode, setToCode] = useState(name && code ? code : '');
  const [widget, setWidget] = useState<null | number>(null);

  const [mousePos, setMousePos] = useState({} as MousePosType);
  const [openOffersDropdown, setOpenOffersDropdown] = useState(false);
  const [hasDropdownOpened, setHasDropdownOpened] = useState(false);

  const cityBoardingPointsListData = {
    mainData: cityBoardingPointsList,
    titlePrefix: `Boarding Points `,
    titleSuffix: `in ${name}`
  };

  const cityDropPointsListData = {
    mainData: cityDropPointsList,
    titlePrefix: `Dropping Points `,
    titleSuffix: `in ${name}`
  };

  const otherRoutesFromCityData = {
    mainData: otherRoutesFromCity,
    titlePrefix: `Other routes `,
    titleSuffix: `from ${name}`
  };

  const otherRoutesToCityData = {
    mainData: otherRoutesToCity,
    titlePrefix: `Other routes `,
    titleSuffix: `to ${name}`
  };

  const nearbyCitiesSourceData = {
    mainData: nearbyCitiesSource,
    titlePrefix: `Popular Cities `,
    titleSuffix: `near ${name}`
  };

  const interlinkingBlockData = [
    cityBoardingPointsListData,
    cityDropPointsListData,
    otherRoutesFromCityData,
    otherRoutesToCityData,
    nearbyCitiesSourceData
  ];

  useEffect(() => {
    if (seoOfferData?.length > 0) {
      const handleMouseMove = debounce(event => {
        if (!hasDropdownOpened) {
          setMousePos({ x: event.clientX, y: event.clientY });
        }
      }, 250);

      window.addEventListener('mousemove', handleMouseMove);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, []);

  useEffect(() => {
    if (
      Object.keys(mousePos).length > 0 &&
      mousePos.y <= 20 &&
      !hasDropdownOpened
    ) {
      setOpenOffersDropdown(true);
      setHasDropdownOpened(true);
    }
  }, [mousePos]);

  const _mqMobile = useMediaQuery({
    query: '(max-device-width: 768px)'
  });
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  const isMobileDevice = isHydrated ? _mqMobile : false;

  const onBusClicked = (fromCity, toCity, fromCode, toCode) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        onFromChange(fromCity, fromCode);
        onToChange(toCity, toCode);
      });
    });
  };
  const onFromChange = (city, code) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setFromCity(city);
        setFromCode(code);
      });
    });
    trackCityTemplateEvent(`Bus_SEO_From_Select`);
  };

  const onToChange = (city, code) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setToCity(city);
        setToCode(code);
      });
    });
    trackCityTemplateEvent(`Bus_SEO_To_Select`);
  };

  useEffect(() => {
    const seeGuildLinesElement = document.getElementById('SeeGuidelines');
    seeGuildLinesElement &&
      seeGuildLinesElement.addEventListener('click', () => {
        trackCityTemplateEvent(
          `Bus_SEO_CityTemplate_MySafety_See_GuideLines_Click`
        );
      });

    const learnMoreElement = document.getElementById('LearnMore');
    learnMoreElement &&
      learnMoreElement.addEventListener('click', () => {
        trackCityTemplateEvent(
          `Bus_SEO_CityTemplate_MySafety_Learn_More_Click`
        );
      });
  }, []);

  return (
    <AppContextProvider
      omniUrlArgs={'source=SEO_City_Template'}
      trackTemplateEvent={trackCityTemplateEvent}
    >
      {schemaInfo && (
        <SchemaInformation
          schemaInformation={schemaInfo as SchemaInformationTypes}
          templateData={templateData}
        />
      )}
      <div className={`busSeoPage`}>
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
        </Helmet>
        <div className={`desktopShow makeSticky`}>
          <BusModifySearch
            sourceCityName={name}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={false}
            demoLink={true}
          />
        </div>
        <div className={`mobileShow makeSticky`}>
          <BusModifySearch
            sourceCityName={name}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={true}
            demoLink={true}
          />
        </div>

        <main className="container">
          <BreadCrumb breadcrumb={templateData.breadcrumb} />
          <PageHeader title={templateData.h1Title} />
          {templateData.extraInfo && (
            <div className="pageExtraInfo font15 appendTop20 appendBottom15">
              {templateData.extraInfo}
            </div>
          )}
          {
            <AdsComponent
              template={'City'}
              placedAt={'top'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: '-10px' }
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom20 adsLayout',
                style: { marginTop: null }
              }}
            />
          }

          {topRoutesToCity.length > 0 && (
            <>
              <section>
                <div
                  style={{ display: 'none' }} // disabled in SSR HTML
                  className="mobileShow"
                >
                  <p className="lineHeight26 fontSize22 latoLight ">{`Top Bus Routes`}</p>
                  <p className="font16 latoBold lineHeight20 blackText paddingB10">
                    to {name}
                  </p>
                </div>
                <div className="desktopShow font22 latoBlack blackText lineHeight26 appendBottom20">
                  <p>{`Top Bus Routes to ${name}`}</p>
                </div>
                <TopBusesRoutes
                  setcollapsableFlag={setcollapsableFlag}
                  onBusClicked={onBusClicked}
                  noOfListings={templateData.noOfListings}
                  type="ToCity"
                  data={topRoutesToCity}
                  name={name}
                  localOperator={null}
                />
              </section>
              <br />
            </>
          )}
          {topRoutesFromCity.length > 0 && (
            <>
              <section>
                <div
                  style={{ display: 'none' }} // disabled in SSR HTML
                  className="mobileShow"
                >
                  <p className="lineHeight26 fontSize22 latoLight ">{`Top Bus Routes`}</p>
                  <p className="font16 latoBold lineHeight20 blackText paddingB10">
                    from {name}
                  </p>
                </div>
                <div className="desktopShow font22 latoBlack blackText lineHeight26 appendBottom20">
                  <p>{`Top Bus Routes from ${name}`}</p>
                </div>
                <TopBusesRoutes
                  setcollapsableFlag={setcollapsableFlag}
                  onBusClicked={onBusClicked}
                  noOfListings={templateData.noOfListings}
                  type="FromCity"
                  data={topRoutesFromCity}
                  name={name}
                  localOperator={null}
                />
              </section>
            </>
          )}
          {
            <AdsComponent
              template={'City'}
              placedAt={'middle'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: '-10px' }
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom15 adsLayout',
                style: { marginTop: null }
              }}
            />
          }
          {topOperatorsList.length > 0 && (
            <section className="visibilityAuto">
              <div
                style={{ display: 'none' }} // disabled in SSR HTML
                className="starRatngWrap__top--mob paddingR16"
              >
                <p className="font22 latoLight appendBottom2">Top Operators</p>
                <p className="font16 latoBold lineHeight20 blackText appendBottom5">
                  {`in ${name}`}
                </p>
              </div>
              <div className="starRatngWrap__top--desk">
                <p>{`Top Operators in ${name}`}</p>
              </div>
              <StarRatingBox topOperatorsList={topOperatorsList} />
            </section>
          )}
          <div className={`busSeoWrap__Details makeFlex visibilityAuto`}>
            {interlinkingBlockData.map((item, idx) => {
              const { mainData, titlePrefix, titleSuffix } = item;
              if (!mainData || mainData?.length < 1) {
                return;
              } else {
                return (
                  <CompactListV1
                    blockData={mainData}
                    titlePrefix={titlePrefix}
                    titleSuffix={titleSuffix}
                    key={idx}
                  />
                );
              }
            })}
          </div>
          <ExtraDescription content={templateData.descriptionInHtml} />
          {description && (
            <section className="visibilityAuto">
              <ThingsToKnow
                text={description}
                title={`Things To Know About`}
                cityName={name}
              />
            </section>
          )}

          {templateData.faq && templateData.faq.length > 0 && (
            <section className="visibilityAuto">
              <FaqSection
                data={templateData.faq}
                pageUrl={templateData.sampleUrl}
                isMobileDevice={isMobileDevice}
                FaqTitle={`Frequently Asked Questions Of ${name} Bus Booking`}
              />
            </section>
          )}
        </main>
        {!isMobileDevice &&
          openOffersDropdown &&
          hasDropdownOpened &&
          seoOfferData.length > 0 && (
            <OfferDropdown
              seoOfferData={seoOfferData}
              setOpenOffersDropdown={setOpenOffersDropdown}
            />
          )}
        <Footer template={templateData} />
        <div
          className="blackOverlay"
          onClick={() => {
            setcollapsableFlag(true);
          }}
        ></div>
      </div>
    </AppContextProvider>
  );
};

CityTemplate.fetchData = async (_, match, urlObj, gRPCClient) => {
  const {
    params: { cityCode }
  } = match;

  const url = `${DATA_LOADER_URL}/api/cityTemplateData/${cityCode}?${urlObj.query}`;
  let responseData;

  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(
      `Unable to fetch city details of ${cityCode}`,
      responseData.error
    );
  }
  return responseData;
};
export default CityTemplate;
